import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "statusCheckbox", "connectivityCheckbox", "deviceTypeCheckbox", "applicationCheckbox",
    "networkGroup", "networkCount", "deviceCount",
    "activeIndicator", "filterButton",
  ]

  static values = {
    localStorageKey: { type: String, default: "huduRadarDeviceFilters" },
  }

  activeStatuses = new Set()
  activeConnectivity = new Set()
  activeDeviceTypes = new Set()
  activeApplications = new Set()

  connect() {
    const restored = this.restoreFiltersFromStorage()
    if (restored) {
      this.setRevealPending(true)
      this.filterDevices({ skipSelectionNavigation: true })
      requestAnimationFrame(() => {
        this.setRevealPending(false)
      })
    }
  }

  toggleStatus(event) {
    const status = event.target.dataset.status
    if (event.target.checked) {
      this.activeStatuses.add(status)
    } else {
      this.activeStatuses.delete(status)
    }
  }

  toggleConnectivity(event) {
    const connectivity = event.target.dataset.connectivity
    if (event.target.checked) {
      this.activeConnectivity.add(connectivity)
    } else {
      this.activeConnectivity.delete(connectivity)
    }
  }

  toggleDeviceType(event) {
    const type = event.target.dataset.deviceType
    if (event.target.checked) {
      this.activeDeviceTypes.add(type)
    } else {
      this.activeDeviceTypes.delete(type)
    }
  }

  toggleApplication(event) {
    const application = event.target.dataset.application
    if (event.target.checked) {
      this.activeApplications.add(application)
    } else {
      this.activeApplications.delete(application)
    }
  }

  apply() {
    this.filterDevices()
    this.saveFiltersToStorage()
  }

  clearAll() {
    this.activeStatuses.clear()
    this.activeConnectivity.clear()
    this.activeDeviceTypes.clear()
    this.activeApplications.clear()

    this.statusCheckboxTargets.forEach(cb => cb.checked = false)
    this.connectivityCheckboxTargets.forEach(cb => cb.checked = false)
    this.deviceTypeCheckboxTargets.forEach(cb => cb.checked = false)
    this.applicationCheckboxTargets.forEach(cb => cb.checked = false)
    this.removeFiltersFromStorage()
    this.filterDevices()
  }

  /**
   * Closes the Radar devices filter dropdown (nested `dropdown` controller) on Escape.
   * Scoped to this controller only; see `devices_list` + `data-action` wiring.
   */
  closeFilterDropdownFromEscape(event) {
    if (event.key !== "Escape") return

    const menu = this.element.querySelector("[data-dropdown-target=\"menu\"]")
    if (!menu || menu.classList.contains("hidden")) return

    const dropdownEl = menu.closest(".dropdown")
    if (!dropdownEl || !this.element.contains(dropdownEl)) return

    const dropdownController = this.application.getControllerForElementAndIdentifier(
      dropdownEl,
      "dropdown"
    )
    if (!dropdownController) return

    event.preventDefault()
    event.stopImmediatePropagation()
    dropdownController.hide()
  }

  filterDevices({ skipSelectionNavigation = false } = {}) {
    const hasStatusFilter = this.activeStatuses.size > 0
    const hasConnectivityFilter = this.activeConnectivity.size > 0
    const hasTypeFilter = this.activeDeviceTypes.size > 0
    const hasApplicationFilter = this.activeApplications.size > 0
    let totalVisible = 0

    this.networkGroupTargets.forEach((group, index) => {
      const devices = group.querySelectorAll("[data-radar-device-selector-target='device']")
      let networkVisible = 0

      devices.forEach(device => {
        const status = device.dataset.status
        const connectivity = device.dataset.connectivity
        const deviceType = device.dataset.deviceType

        const statusMatch = !hasStatusFilter || this.activeStatuses.has(status)
        const connectivityMatch = !hasConnectivityFilter || this.activeConnectivity.has(connectivity)
        const typeMatch = !hasTypeFilter || this.activeDeviceTypes.has(deviceType)
        const applicationMatch = !hasApplicationFilter || [...this.activeApplications].some((application) => {
          if (application === "has_agent") return device.dataset.hasAgent === "true"
          if (application === "has_service") return device.dataset.hasService === "true"
          return false
        })

        if (statusMatch && connectivityMatch && typeMatch && applicationMatch) {
          device.style.display = ""
          networkVisible++
        } else {
          device.style.display = "none"
        }
      })

      totalVisible += networkVisible

      const networkCountEl = this.networkCountTargets.find(el =>
        el.dataset.networkIndex === String(index)
      )
      if (networkCountEl) {
        networkCountEl.textContent = `(${networkVisible})`
      }
    })

    if (this.hasDeviceCountTarget) {
      this.deviceCountTarget.textContent = `(${totalVisible})`
    }

    this.updateActiveIndicator()
    this.updateSelectionAfterFilter(totalVisible, { skipNavigation: skipSelectionNavigation })
  }

  updateSelectionAfterFilter(totalVisible, { skipNavigation = false } = {}) {
    if (totalVisible === 0) {
      this.clearDevicePanel()
      return
    }

    const selected = this.element.querySelector(".radar-interface__device-item--selected")
    const selectedFilteredOut = selected && selected.style.display === "none"

    if (!selectedFilteredOut) return

    const firstVisible = this.getVisibleFilteredDevices()[0]
    if (firstVisible) {
      this.selectDevice(firstVisible, { navigate: !skipNavigation })
    }
  }

  selectDevice(device, { navigate = true } = {}) {
    const selectorController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "radar-device-selector"
    )

    if (selectorController) {
      selectorController.deviceTargets.forEach(el => {
        el.classList.remove("radar-interface__device-item--selected")
      })
      device.classList.add("radar-interface__device-item--selected")
      selectorController.updateTabIndexes()

      if (navigate) {
        selectorController.pendingDevice = device
        selectorController.scheduleClick()
      }
      return
    }

    this.element.querySelectorAll(".radar-interface__device-item--selected")
      .forEach(el => el.classList.remove("radar-interface__device-item--selected"))
    device.classList.add("radar-interface__device-item--selected")

    if (navigate) {
      device.click()
    }
  }

  getVisibleFilteredDevices() {
    return [...this.element.querySelectorAll("[data-radar-device-selector-target='device']")]
      .filter(el => el.style.display !== "none" && el.offsetParent !== null)
  }

  clearDevicePanel() {
    this.element.querySelectorAll(".radar-interface__device-item--selected")
      .forEach(el => el.classList.remove("radar-interface__device-item--selected"))

    const selectorController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "radar-device-selector"
    )
    selectorController?.updateTabIndexes()

    const panel = document.getElementById("device_panel")
    if (!panel) return

    panel.removeAttribute("data-current-device-id")
    panel.innerHTML = `
      <div class="radar-interface__details__device-info">
        <div class="radar-interface__details__device-info__content">
          <p class="radar-interface__message">Select a device to view details.</p>
        </div>
      </div>
    `
  }

  saveFiltersToStorage() {
    if (!this.hasLocalStorageKeyValue) return

    const hasFilters = this.activeStatuses.size > 0 ||
      this.activeConnectivity.size > 0 ||
      this.activeDeviceTypes.size > 0 ||
      this.activeApplications.size > 0

    if (!hasFilters) {
      this.removeFiltersFromStorage()
      return
    }

    try {
      localStorage.setItem(this.localStorageKeyValue, JSON.stringify({
        statuses: [...this.activeStatuses],
        connectivity: [...this.activeConnectivity],
        deviceTypes: [...this.activeDeviceTypes],
        applications: [...this.activeApplications],
      }))
    } catch {
      // Persistence is best-effort; filtering still works when storage is unavailable.
    }
  }

  removeFiltersFromStorage() {
    if (!this.hasLocalStorageKeyValue) return

    try {
      localStorage.removeItem(this.localStorageKeyValue)
    } catch {
      // Persistence is best-effort; clearing filters still works when storage is unavailable.
    }
  }

  setRevealPending(pending) {
    this.element.classList.toggle("radar-interface__devices-list--filter-pending", pending)
  }

  filterListFromStorage(value) {
    return Array.isArray(value) ? value : []
  }

  restoreFiltersFromStorage() {
    if (!this.hasLocalStorageKeyValue) return false

    try {
      const stored = localStorage.getItem(this.localStorageKeyValue)
      if (!stored) return false

      const parsed = JSON.parse(stored)
      const statuses = this.filterListFromStorage(parsed?.statuses)
      const connectivity = this.filterListFromStorage(parsed?.connectivity)
      const deviceTypes = this.filterListFromStorage(parsed?.deviceTypes)
      const applications = this.filterListFromStorage(parsed?.applications)
      if (!statuses.length && !connectivity.length && !deviceTypes.length && !applications.length) return false

      this.activeStatuses = new Set(statuses)
      this.activeConnectivity = new Set(connectivity)
      this.activeDeviceTypes = new Set(deviceTypes)
      this.activeApplications = new Set(applications)

      this.statusCheckboxTargets.forEach(cb => {
        cb.checked = this.activeStatuses.has(cb.dataset.status)
      })
      this.connectivityCheckboxTargets.forEach(cb => {
        cb.checked = this.activeConnectivity.has(cb.dataset.connectivity)
      })
      this.deviceTypeCheckboxTargets.forEach(cb => {
        cb.checked = this.activeDeviceTypes.has(cb.dataset.deviceType)
      })
      this.applicationCheckboxTargets.forEach(cb => {
        cb.checked = this.activeApplications.has(cb.dataset.application)
      })

      return true
    } catch {
      return false
    }
  }

  updateActiveIndicator() {
    const count = this.activeStatuses.size + this.activeConnectivity.size +
      this.activeDeviceTypes.size + this.activeApplications.size

    if (this.hasActiveIndicatorTarget) {
      if (count > 0) {
        this.activeIndicatorTarget.classList.remove("hidden")
      } else {
        this.activeIndicatorTarget.classList.add("hidden")
      }
    }

    if (this.hasFilterButtonTarget) {
      const btn = this.filterButtonTarget
      const defaultLabel = btn.dataset.defaultAriaLabel
      const activeLabel = btn.dataset.activeAriaLabel
      if (defaultLabel && activeLabel) {
        btn.setAttribute("aria-label", count > 0 ? activeLabel : defaultLabel)
      }
    }
  }
}
